import * as React from "react";
import { FaPlus } from "react-icons/fa";
import styled from "styled-components";
import { ImagePartIcon } from "../../../../../components/utils/Icons/Part/ImagePartIcon";
import ExplanationIcon from "../../../../../components/utils/Icons/Part/Explanation";
import GapSentenceIcon from "../../../../../components/utils/Icons/Part/GapSentence";
import ListItemWithOrderIcon from "../../../../../components/utils/Icons/Part/ListItemWithOrder";
import ListItemWithoutOrderIcon from "../../../../../components/utils/Icons/Part/ListItemWithoutOrder";
import MultipleChoiceIcon from "../../../../../components/utils/Icons/Part/MultipleChoice";
import OneOneLayoutIcon from "../../../../../components/utils/Icons/Part/OneOneLayout";
import OneOneOneLayoutIcon from "../../../../../components/utils/Icons/Part/OneOneOneLayout";
import OneOneTwoLayoutIcon from "../../../../../components/utils/Icons/Part/OneOneTwoLayout";
import OneThreeLayoutIcon from "../../../../../components/utils/Icons/Part/OneThreeLayout";
import OneTwoOneLayoutIcon from "../../../../../components/utils/Icons/Part/OneTwoOneLayout";
import OrderItemsIcon from "../../../../../components/utils/Icons/Part/OrderItems";
import { DividerIcon, SeparatorIcon, SpacerIcon } from "../../../../../components/utils/Icons/Part/SeparatorIcons";
import SingleChoiceIcon from "../../../../../components/utils/Icons/Part/SingleChoice";
import ThreeColumnGridIcon from "../../../../../components/utils/Icons/Part/ThreeColumnGrid";
import ThreeOneLayoutIcon from "../../../../../components/utils/Icons/Part/ThreeOneLayout";
import TwoColumnGridIcon from "../../../../../components/utils/Icons/Part/TwoColumnsGrid";
import TwoOneOneLayoutIcon from "../../../../../components/utils/Icons/Part/TwoOneOneLayout";
import { CourseComponentType } from "../../../../course-author/types";
import ClickableElement from "../../../../utils/ClickableElement";
import SvgSpinner from "../../../../utils/SVGSpinner/SvgSpinner";
import { DocumentPartIcon } from "../../../../../components/utils/Icons/Part/Document";
import { DropZone, IDropZoneProps } from "../../../../../context/UseDragAndDrop";

interface IAddInstructionPartProps {
  className?: string;
  addPart: (type: CourseComponentType, position?: number, properties?: any) => void;
  displayOnStart: boolean;
  position?: number;
  includeExplanation: boolean;
  includeGrids: boolean;
  includeImages?: boolean;
  includeDocuments?: boolean;
}

type AddInstructionPartWithDropZoneProps = IAddInstructionPartProps & IDropZoneProps;

const Row = styled.div<{ isHover: boolean }>`
  display: flex;
  justify-content: flex-start;
  margin: 2.5rem 0;
`;

export const Title = styled.p`
  color: ${({ theme }) => theme.colors.lightsilver};
  font-style: italic;
  font-weight: bold;
`;

const DisplayButtons = styled(ClickableElement)`
  width: 3rem;
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
`;

export const AddPartButton = styled(ClickableElement)`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-around;

  border-radius: 1rem;

  height: 14rem;
  width: 11rem;
  margin: 0 0.5rem 0 0;

  transition: background 0.25s ease;

  font-size: 13px;
  font-weight: 600;

  background: ${({ theme }) => theme.colors.lightgrey3};
  color: ${({ theme }) => theme.colors.darkblue};

  &:hover {
    background: ${({ theme }) => theme.colors.lightsilver};
  }
`;

const GridLayoutRow = styled(ButtonRow)`
  border-radius: 0rem 1rem 1rem 1rem;
  background-color: ${({ theme }) => theme.colors.lightgrey};
  padding: 2rem 0.5rem 1rem 1rem;
  z-index: 10;
  margin-left: -0.5rem;

  &:before {
    content: "";
    position: absolute;
    width: 1rem;
    height: 1rem;
    background: ${({ theme }) => theme.colors.lightgrey};
    z-index: 10;
    top: -1rem;
    left: 11.5rem;
  }

  &:after {
    content: "";
    position: absolute;
    width: 1rem;
    height: 1rem;
    background: white;
    z-index: 10;
    top: -1rem;
    left: 11.5rem;
    border-bottom-left-radius: 1rem;
  }
`;

const HoverTitle = styled.p`
  font-size: small;
  position: absolute;
  margin-left: 0.75rem;
  text-align: center;
  white-space: nowrap;
  font-style: italic;
  color: ${({ theme }) => theme.colors.darkblue};
  top: -2rem;
`;

interface IHoverWithOptionsProps {
  part: React.ElementType;
  options: React.ReactElement;
  setIsHover: React.Dispatch<React.SetStateAction<boolean>>;
}

// TODO: The second isHover is the outer state to avoid outer elements to move when hover
// this could be avoided using better css but will be address on GRC-514
export const PartWithHoverAndOptions: React.FC<IHoverWithOptionsProps> = ({
  part: Element,
  options,
  setIsHover: setIsHoverOuter,
}: IHoverWithOptionsProps) => {
  const [isHover, setIsHover] = React.useState(false);
  const onMouseEnter = () => {
    setIsHoverOuter(true);
    setIsHover(true);
  };
  const onMouseLeave = () => {
    setIsHoverOuter(false);
    setIsHover(false);
  };

  const HoverElement = styled.div<{ _isHover: boolean }>`
    border-radius: 1rem;
    ${({ _isHover }) =>
      _isHover
        ? `
      padding-left: 0.5rem;
      padding-top: 0.5rem;
      margin-right: 0.5rem;
      padding-bottom: 2rem;
    `
        : ""}
    background-color: ${({ theme, _isHover }) => (_isHover ? theme.colors.lightgrey : "")};
  `;

  const OptionsContainer = styled.div`
    position: absolute;
    display: flex;
    justify-content: flex-start;
    margin-top: 1rem;
  `;

  return (
    <HoverElement _isHover={isHover} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onMouseLeave}>
      <Element onMouseLeave={onMouseLeave} />
      {isHover && <OptionsContainer onMouseLeave={onMouseLeave}> {options} </OptionsContainer>}
    </HoverElement>
  );
};

export const AddInstructionPart: React.FC<IAddInstructionPartProps> = ({
  includeImages = true,
  includeDocuments = true,
  ...props
}) => {
  const [isPending, setPending] = React.useState(false);
  const [isHover, setIsHover] = React.useState(false);
  const [display, setDisplay] = React.useState(false);
  const toggleDisplay = () => setDisplay(!display);
  const addPart = (type: CourseComponentType, properties?: any) => () => {
    setPending(true);
    props.addPart(type, props.position, properties);
    !props.displayOnStart && toggleDisplay();
    setPending(false);
  };

  const addGridPart = (tag: string, layoutContent: number[], content: any[]) =>
    addPart(CourseComponentType.Grid, {
      content: {
        columns: {
          content,
          tag,
        },
        layout: { content: layoutContent, tag },
      },
    });

  const TwoGridButton = () => (
    <AddPartButton>
      <TwoColumnGridIcon /> Grid (two columns)
    </AddPartButton>
  );

  const ThreeGridButton = () => (
    <AddPartButton>
      <ThreeColumnGridIcon /> Grid (three columns)
    </AddPartButton>
  );

  const SeparatorButton = () => (
    <AddPartButton>
      <SeparatorIcon /> Separator
    </AddPartButton>
  );

  const TwoColumnLayoutOptions = (
    <GridLayoutRow>
      <HoverTitle> Select a layout </HoverTitle>
      <AddPartButton onClick={addGridPart("TwoColumns", [1, 1], [null, null])}>
        <OneOneLayoutIcon />
        1-1
      </AddPartButton>
      <AddPartButton onClick={addGridPart("TwoColumns", [1, 3], [null, null])}>
        <OneThreeLayoutIcon />
        1-3
      </AddPartButton>
      <AddPartButton onClick={addGridPart("TwoColumns", [3, 1], [null, null])}>
        <ThreeOneLayoutIcon />
        3-1
      </AddPartButton>
    </GridLayoutRow>
  );

  const ThreeColumnLayoutOptions = (
    <GridLayoutRow>
      <HoverTitle> Select a layout </HoverTitle>
      <AddPartButton onClick={addGridPart("ThreeColumns", [1, 1, 1], [null, null, null])}>
        <OneOneOneLayoutIcon />
        1-1-1
      </AddPartButton>
      <AddPartButton onClick={addGridPart("ThreeColumns", [1, 2, 1], [null, null, null])}>
        <OneTwoOneLayoutIcon />
        1-2-1
      </AddPartButton>
      <AddPartButton onClick={addGridPart("ThreeColumns", [1, 1, 2], [null, null, null])}>
        <OneOneTwoLayoutIcon />
        1-1-2
      </AddPartButton>
      <AddPartButton onClick={addGridPart("ThreeColumns", [2, 1, 1], [null, null, null])}>
        <TwoOneOneLayoutIcon />
        2-1-1
      </AddPartButton>
    </GridLayoutRow>
  );

  const TwoSeparatorLayoutOptions = (
    <GridLayoutRow>
      <HoverTitle> Select a style </HoverTitle>
      <AddPartButton onClick={addPart(CourseComponentType.Spacer)}>
        <SpacerIcon />
        Spacer
      </AddPartButton>
      <AddPartButton onClick={addPart(CourseComponentType.Divider)}>
        <DividerIcon />
        Divider
      </AddPartButton>
    </GridLayoutRow>
  );

  React.useEffect(() => {
    if (props.includeGrids) {
      setPending(false);
    }
  }, [isPending]);

  if (isPending) {
    return (
      <div
        style={{
          display: "grid",
          height: "100%",
          placeContent: "center",
          width: "100%",
        }}
      >
        <SvgSpinner />
      </div>
    );
  }

  return (
    <div className={props.className}>
      {(props.displayOnStart || display) && <Title>Add a learning block</Title>}
      <Row isHover={isHover}>
        {!props.displayOnStart && (
          <DisplayButtons onClick={toggleDisplay}>
            <FaPlus aria-label="open-parts" />
          </DisplayButtons>
        )}
        {(props.displayOnStart || display) && (
          <ButtonRow>
            {props.includeGrids && (
              <>
                <PartWithHoverAndOptions
                  part={TwoGridButton}
                  options={TwoColumnLayoutOptions}
                  setIsHover={setIsHover}
                />
                <PartWithHoverAndOptions
                  part={ThreeGridButton}
                  options={ThreeColumnLayoutOptions}
                  setIsHover={setIsHover}
                />
              </>
            )}
            {props.includeExplanation && (
              <AddPartButton onClick={addPart(CourseComponentType.Teaching)}>
                <ExplanationIcon />
                Explanation
              </AddPartButton>
            )}
            {includeImages && (
              <AddPartButton onClick={addPart(CourseComponentType.Image)}>
                <ImagePartIcon />
                Image
              </AddPartButton>
            )}
            {includeDocuments && (
              <AddPartButton onClick={addPart(CourseComponentType.Document)}>
                <DocumentPartIcon />
                Document
              </AddPartButton>
            )}
            {props.includeGrids && (
              <PartWithHoverAndOptions
                part={SeparatorButton}
                options={TwoSeparatorLayoutOptions}
                setIsHover={setIsHover}
              />
            )}
            <AddPartButton
              onClick={addPart(CourseComponentType.Choice, {
                content: { singleChoice: false },
              })}
            >
              <MultipleChoiceIcon />
              Multiple Choice
            </AddPartButton>
            <AddPartButton
              onClick={addPart(CourseComponentType.Choice, {
                content: { singleChoice: true },
              })}
            >
              <SingleChoiceIcon />
              Single Choice
            </AddPartButton>
            <AddPartButton onClick={addPart(CourseComponentType.Cloze)}>
              <GapSentenceIcon />
              Gap Sentence
            </AddPartButton>
            <AddPartButton onClick={addPart(CourseComponentType.UnorderedList)}>
              <ListItemWithoutOrderIcon />
              List Items
            </AddPartButton>
            <AddPartButton onClick={addPart(CourseComponentType.ListWithoutOptions)}>
              <ListItemWithOrderIcon />
              List Items (in order)
            </AddPartButton>
            <AddPartButton onClick={addPart(CourseComponentType.ListWithOptions)}>
              <OrderItemsIcon />
              Order Items
            </AddPartButton>
          </ButtonRow>
        )}
      </Row>
    </div>
  );
};

export const AddInstructionPartWithDropZone: React.FC<AddInstructionPartWithDropZoneProps> = ({
  dropPosition,
  dropZoneType,
  newInstructionType,
  editOperations,
  path,
  instructionSlide,
  includeImages = true,
  ...addInstructionprops
}) => (
  <DropZone
    dropPosition={dropPosition}
    dropZoneType={dropZoneType}
    newInstructionType={newInstructionType}
    editOperations={editOperations}
    path={path}
    instructionSlide={instructionSlide}
  >
    <AddInstructionPart {...addInstructionprops} includeImages={includeImages} />
  </DropZone>
);
